// src/pages/YesNoTarot.js
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/seo"; // <-- uses the improved Seo component

/* ==============================
   THEME (background + button)
============================== */
const THEME = {
 radial: "rgba(100, 183, 255, 0.22)",
    top: "#295997ff",
    l1: "#24476f",
    l2: "#3a6ea5",
    btnFrom: "#3a6ea5",
    btnTo: "#1d3557",
    accent: "#b8d9ff",
};

/* ==============================
   API BASE
============================== */
const API_BASE =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_BASE) ||
  "https://tarotfate-api.tarotfate-com.workers.dev";

/* ==============================
   Card groups (provided)
============================== */
function useGroupedCards() {
  return useMemo(() => {
    const groups = {
      "Major Arcana": [
        "the-fool-tarot-card-reading-meaning",
        "the-magician-tarot-card-reading-meaning",
        "the-high-priestess-tarot-card-reading-meaning",
        "the-empress-tarot-card-reading-meaning",
        "the-emperor-tarot-card-reading-meaning",
        "the-hierophant-tarot-card-reading-meaning",
        "the-lovers-tarot-card-reading-meaning",
        "the-chariot-tarot-card-reading-meaning",
        "strength-tarot-card-reading-meaning",
        "the-hermit-tarot-card-reading-meaning",
        "wheel-of-fortune-tarot-card-reading-meaning",
        "justice-tarot-card-reading-meaning",
        "the-hanged-man-tarot-card-reading-meaning",
        "death-tarot-card-reading-meaning",
        "temperance-tarot-card-reading-meaning",
        "the-devil-tarot-card-reading-meaning",
        "the-tower-tarot-card-reading-meaning",
        "the-star-tarot-card-reading-meaning",
        "the-moon-tarot-card-reading-meaning",
        "the-sun-tarot-card-reading-meaning",
        "judgement-tarot-card-reading-meaning",
        "the-world-tarot-card-reading-meaning",
      ],
      Wands: [
        "ace-of-wands-tarot-card-reading-meaning",
        "two-of-wands-tarot-card-reading-meaning",
        "three-of-wands-tarot-card-reading-meaning",
        "four-of-wands-tarot-card-reading-meaning",
        "five-of-wands-tarot-card-reading-meaning",
        "six-of-wands-tarot-card-reading-meaning",
        "seven-of-wands-tarot-card-reading-meaning",
        "eight-of-wands-tarot-card-reading-meaning",
        "nine-of-wands-tarot-card-reading-meaning",
        "ten-of-wands-tarot-card-reading-meaning",
        "page-of-wands-tarot-card-reading-meaning",
        "knight-of-wands-tarot-card-reading-meaning",
        "queen-of-wands-tarot-card-reading-meaning",
        "king-of-wands-tarot-card-reading-meaning",
      ],
      Cups: [
        "ace-of-cups-tarot-card-reading-meaning",
        "two-of-cups-tarot-card-reading-meaning",
        "three-of-cups-tarot-card-reading-meaning",
        "four-of-cups-tarot-card-reading-meaning",
        "five-of-cups-tarot-card-reading-meaning",
        "six-of-cups-tarot-card-reading-meaning",
        "seven-of-cups-tarot-card-reading-meaning",
        "eight-of-cups-tarot-card-reading-meaning",
        "nine-of-cups-tarot-card-reading-meaning",
        "ten-of-cups-tarot-card-reading-meaning",
        "page-of-cups-tarot-card-reading-meaning",
        "knight-of-cups-tarot-card-reading-meaning",
        "queen-of-cups-tarot-card-reading-meaning",
        "king-of-cups-tarot-card-reading-meaning",
      ],
      Swords: [
        "ace-of-swords-tarot-card-reading-meaning",
        "two-of-swords-tarot-card-reading-meaning",
        "three-of-swords-tarot-card-reading-meaning",
        "four-of-swords-tarot-card-reading-meaning",
        "five-of-swords-tarot-card-reading-meaning",
        "six-of-swords-tarot-card-reading-meaning",
        "seven-of-swords-tarot-card-reading-meaning",
        "eight-of-swords-tarot-card-reading-meaning",
        "nine-of-swords-tarot-card-reading-meaning",
        "ten-of-swords-tarot-card-reading-meaning",
        "page-of-swords-tarot-card-reading-meaning",
        "knight-of-swords-tarot-card-reading-meaning",
        "queen-of-swords-tarot-card-reading-meaning",
        "king-of-swords-tarot-card-reading-meaning",
      ],
      Pentacles: [
        "ace-of-pentacles-tarot-card-reading-meaning",
        "two-of-pentacles-tarot-card-reading-meaning",
        "three-of-pentacles-tarot-card-reading-meaning",
        "four-of-pentacles-tarot-card-reading-meaning",
        "five-of-pentacles-tarot-card-reading-meaning",
        "six-of-pentacles-tarot-card-reading-meaning",
        "seven-of-pentacles-tarot-card-reading-meaning",
        "eight-of-pentacles-tarot-card-reading-meaning",
        "nine-of-pentacles-tarot-card-reading-meaning",
        "ten-of-pentacles-tarot-card-reading-meaning",
        "page-of-pentacles-tarot-card-reading-meaning",
        "knight-of-pentacles-tarot-card-reading-meaning",
        "queen-of-pentacles-tarot-card-reading-meaning",
        "king-of-pentacles-tarot-card-reading-meaning",
      ],
    };
    return groups;
  }, []);
}

/* ==============================
   Name/slug helpers
============================== */
function normalizeName(name = "") {
  return String(name).toLowerCase().replace(/^[\s]*the\s+/, "").replace(/\s+/g, " ").trim();
}
function properCaseNameFromSlug(slug) {
  const base = slug.replace(/-tarot-card-reading-meaning$/i, "");
  const tokens = base.split("-");
  const small = new Set(["of", "the"]);
  const fix = {
    ace: "Ace", two: "Two", three: "Three", four: "Four", five: "Five",
    six: "Six", seven: "Seven", eight: "Eight", nine: "Nine", ten: "Ten",
    page: "Page", knight: "Knight", queen: "Queen", king: "King",
    wheel: "Wheel", fortune: "Fortune", judgement: "Judgement",
    strength: "Strength", justice: "Justice", temperance: "Temperance",
    death: "Death", moon: "Moon", sun: "Sun", star: "Star", world: "World",
    lovers: "Lovers", hermit: "Hermit", chariot: "Chariot", devil: "Devil",
    tower: "Tower", empress: "Empress", emperor: "Emperor", hierophant: "Hierophant",
    magician: "Magician", priestess: "Priestess", hanged: "Hanged", man: "Man",
    cups: "Cups", wands: "Wands", swords: "Swords", pentacles: "Pentacles",
    fool: "Fool", high: "High", the: "The", of: "of"
  };
  const words = tokens.map((t) => {
    const low = t.toLowerCase();
    if (fix[low]) return fix[low];
    if (small.has(low)) return low;
    return low.charAt(0).toUpperCase() + low.slice(1);
  });
  const hasLeadingThe = /^the-/.test(base);
  const name = words.join(" ").replace(/\s+of\s+/g, " of ").replace(/\s+the\s+/g, " the ");
  return hasLeadingThe && !/^The\s/i.test(name) ? `The ${name.replace(/^the\s/i, "")}` : name;
}
function useCardIndex() {
  const groups = useGroupedCards();
  return useMemo(() => {
    const byName = new Map();
    Object.entries(groups).forEach(([suit, slugs]) => {
      slugs.forEach((slug) => {
        const name = properCaseNameFromSlug(slug);
        const norm = normalizeName(name);
        byName.set(norm, { slug, suit, name });
        const withoutThe = norm.replace(/^[\s]*the\s+/, "");
        byName.set(withoutThe, { slug, suit, name });
      });
    });
    return { byName };
  }, [groups]);
}
function findByCardName(index, displayName) {
  if (!displayName) return null;
  const norm = normalizeName(displayName);
  return index.byName.get(norm) || null;
}
function cardImageUrlFromSlug(slug) {
  return `/card/${slug}.webp`;
}

/* ==============================
   Deterministic RNG
============================== */
function seedFromQuestion(q) {
  const s = (q || "").trim().toLowerCase();
  const d = new Date();
  const dayKey = `${d.getUTCFullYear()}-${d.getUTCMonth() + 1}-${d.getUTCDate()}`;
  let h = 2166136261;
  const str = s + "|" + dayKey;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24);
  }
  return Math.abs(h >>> 0);
}
function rng(n, seed) {
  let x = seed || 2463534242;
  x ^= x << 13; x ^= x >>> 17; x ^= x << 5;
  return Math.abs(x >>> 0) % n;
}

/* ==============================
   Outcome pools
============================== */
const YES_NAMES = [
  "The Sun","The Star","The World","The Lovers","The Empress",
  "The Magician","Strength","Ace of Cups","Ace of Wands",
  "Two of Cups","Four of Wands","Six of Wands","Ten of Cups",
  "Ten of Pentacles","Nine of Cups"
];
const NO_NAMES = [
  "The Tower","Death","The Devil","Five of Cups","Five of Pentacles",
  "Three of Swords","Seven of Swords","Eight of Swords",
  "Ten of Swords","Five of Wands","Nine of Swords","Four of Cups",
  "Two of Swords","Seven of Cups","Five of Swords"
];
const WAIT_NAMES = [
  "The Hanged Man","Temperance","The Hermit","Justice","The Moon",
  "Wheel of Fortune","Two of Pentacles","Four of Pentacles",
  "Seven of Pentacles","Page of Cups","Page of Pentacles",
  "Knight of Pentacles","Two of Wands","Three of Wands","The High Priestess"
];

/* ==============================
   Validation (close-ended + timeboxed + anti-gibberish)
============================== */
function isGibberishWord(w) {
  const word = w.toLowerCase().replace(/[^a-z]/g, "");
  if (!word || word.length < 4) return false;

  // whitelist a few tricky real words that have few vowels
  const whitelist = new Set(["rhythm", "rhythms", "sphinx", "lynch", "crypts", "myths"]);
  if (whitelist.has(word)) return false;

  const vowels = (word.match(/[aeiouy]/g) || []).length;
  const consonantRun = /[bcdfghjklmnpqrstvwxyz]{5,}/i.test(word); // 5+ consonants in a row
  const tripleRepeat = /(.)\1{2,}/.test(word);                     // e.g., heeey
  const keyboardMash = /^[asdfjkl]{5,}$/i.test(word);              // only from home-row cluster

  // Long with almost no vowels
  const veryFewVowels = word.length >= 7 && vowels <= 1;

  return consonantRun || tripleRepeat || keyboardMash || veryFewVowels;
}

function validateQuestion(raw = "") {
  const s = String(raw).trim();

  if (!s) return "Please enter a question.";
  if ((s.match(/\?/g) || []).length > 1) return "One question at a time — use only one question mark.";
  if (!s.endsWith("?")) return "Finish with a question mark (?).";
  if (s.length < 5) return "Add a little more detail so the cards can focus.";
  if (s.length > 160) return "Keep it concise (under 160 characters).";

  // Must be close-ended
  const startsYesNo = /^(will|shall|should|is|are|am|do|does|did|can|could|may|might|have|has|had|was|were|would)\b/i.test(s);
  if (!startsYesNo) {
    return "Make it close-ended. Start with: Will, Should, Is, Are, Do, Does, Can, Would…";
  }

  // Block wh-words (open questions)
  if (/\b(why|how|what|when|where|which|who|whom|whose)\b/i.test(s)) {
    return "Keep it close-ended (answerable by Yes / No / Wait).";
  }

  // Anti-gibberish
  const stops = new Set([
    "i","me","my","mine","you","your","yours","he","him","his","she","her","hers",
    "we","us","our","ours","they","them","their","theirs",
    "will","shall","should","is","are","am","do","does","did","can","could",
    "may","might","have","has","had","was","were","would",
    "a","an","the","to","for","of","and","or","by","on","in","at","with",
    "this","that","these","those","from","about","as","be","been"
  ]);

  const tokens = s.replace(/[?!.]/g, "").split(/\s+/);
  for (const t of tokens) {
    const bare = t.toLowerCase().replace(/[^a-z]/g, "");
    if (!bare || stops.has(bare)) continue;
    if (isGibberishWord(bare)) {
      return "Please remove placeholder text (keyboard mashes or nonsense) and ask your real question.";
    }
  }

  return null; // valid
}

const FALLBACK_TEXT = {
  YES: "The path is open. Act with clarity and kindness, and follow through.",
  NO: "Not at this time. Something essential needs care, mending, or a new approach.",
  WAIT: "Pause and observe. Let a piece of truth reveal itself before you move.",
};

/* ==============================
   Page
============================== */
export default function YesNoTarot() {
  const { byName } = useCardIndex();

  const [q, setQ] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [isFlipping, setIsFlipping] = useState(false);
  const [result, setResult] = useState(null); // { verdict, cardName, slug, suit, reason }
  const [loadingReason, setLoadingReason] = useState(false);
  const [error, setError] = useState("");
  const [showCopiedToast, setShowCopiedToast] = useState(false);

  // live validation
  useEffect(() => {
    if (!q) { setError(""); return; }
    setError(validateQuestion(q) || "");
  }, [q]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((s) => Math.max(s - 1, 0)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const draw = async () => {
    const msg = validateQuestion(q);
    if (msg) { setError(msg); return; }
    if (cooldown > 0) return;

    setIsFlipping(true);

    const seed = seedFromQuestion(q);
    const bucket = rng(100, seed);
    let verdict;
    if (bucket < 40) verdict = "YES";
    else if (bucket < 75) verdict = "WAIT";
    else verdict = "NO";

    const pool = verdict === "YES" ? YES_NAMES : verdict === "NO" ? NO_NAMES : WAIT_NAMES;
    const pickedName = pool[rng(pool.length, seed + 17)];
    const found = findByCardName({ byName }, pickedName);

    setTimeout(async () => {
      setIsFlipping(false);
      setCooldown(60);

      let reason = FALLBACK_TEXT[verdict];

      try {
        setLoadingReason(true);
        const r = await fetch(`${API_BASE}/api/yesno`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            question: q.trim(),
            verdict,
            cardName: pickedName,
            cardSlug: found?.slug || null,
          }),
        });
        const data = await r.json().catch(() => ({}));
        if (r.ok && (data?.reason || data?.note || data?.reading)) {
          reason = data.reason || data.note || data.reading;
        }
      } catch {
        // keep fallback
      } finally {
        setLoadingReason(false);
      }

      setResult({
        verdict,
        cardName: pickedName,
        slug: found?.slug || null,
        suit: found?.suit || null,
        reason,
      });
    }, 500);
  };

  const drawDisabled = !!validateQuestion(q) || cooldown > 0;

  // ===== SEO bits (page-level) =====
  const seoTitle = "Yes / No Tarot | Instant One‑Card Answers"; // Title template is applied in <Seo/>
  const seoDesc = "Ask a focused question and draw one card for a clear Yes, No, or Wait — with a short, reflective message to guide your next step.";
  const seoPath = "/yes-no-tarot";
  const ogImage = "/icons/yesno.webp"; // add this asset under /public/icons/
  const schemaOverrides = [
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Tarot", item: "https://tarotfate.com/tarot-cards" },
        { "@type": "ListItem", position: 2, name: "Yes / No Tarot", item: "https://tarotfate.com/yes-no-tarot" },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "How does Yes / No Tarot work?",
          acceptedAnswer: { "@type": "Answer", text: "Ask one close‑ended question, draw a single card, and receive a Yes, No, or Wait verdict with a short reflection. It’s meant for quick direction, not prediction." },
        },
        {
          "@type": "Question",
          name: "How often can I ask?",
          acceptedAnswer: { "@type": "Answer", text: "Give it at least a minute before you ask again — ideally, refine the question or wait until the situation changes." },
        },
      ],
    },
  ];

  const verdictOnWhiteColor =
    result?.verdict === "YES"
      ? "text-emerald-700"
      : result?.verdict === "NO"
      ? "text-rose-700"
      : "text-amber-700";

  return (
    <div
      className="min-h-screen"
      style={{
        background:
          `radial-gradient(1200px 1000px at 50% -10%, ${THEME.radial} 10%, ${THEME.top} 35%), ` +
          `linear-gradient(180deg, ${THEME.l1} 40%, ${THEME.l2} 50%)`,
      }}
    >
      {/* SEO head */}
      <Seo
        title={seoTitle}
        description={seoDesc}
        path={seoPath}
        image={ogImage}
        schema={schemaOverrides}
        keywords="yes or no tarot, one card tarot, instant tarot, decision guidance, quick tarot reading"
      />

      <main className="relative z-10 max-w-5xl mx-auto px-2 py-4 text-white/90 font-[Cormorant_Garamond]">
        {/* Logo */}
        <div className="flex justify-center">
          <img
src="/icons/yesno.webp"
alt="Yes / No Tarot"
className="w-32 h-auto sm:w-36 md:w-40 lg:w-44 object-contain mx-auto select-none pointer-events-none"
onContextMenu={(e) => e.preventDefault()}
draggable="false"
/>
        </div>

        {/* Main panel */}
        <section className="mt-4 rounded-lg border border-white/45 bg-gradient-to-b from-[#03779B]/90 via-[#0C4B84]/100 to-[#B07E14]/1  backdrop-blur-sm shadow-[0_3px_10px_rgba(0,0,0,0.45)]">
          <div className="p-6 sm:p-8">
            {/* Input */}
            <label className="block text-sm uppercase tracking-widest text-white/90 text-center">
              Your Question <span className="text-white font-semibold">(required)</span>
            </label>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder='Ask a question...'
              className={`mt-2 w-full rounded-md bg-white text-[#0f3d3f] px-4 py-2 outline-none border ${
                error ? "border-rose-400 focus:border-rose-400" : "border-[#0f3d3f]/30 focus:border-[#0f3d3f]"
              }`}
              onKeyDown={(e) => { if (e.key === "Enter") draw(); }}
              aria-invalid={!!error}
              aria-describedby={error ? "yesno-error" : undefined}
            />
            <div className="mt-2 text-sm text-white/80 text-center italic">
              e.g. Should I reach out this week? <span className="opacity-70"></span>
            </div>
            {error && (
              <div id="yesno-error" role="alert" className="mt-2 text-sm text-rose-200 text-center" aria-live="polite">
                {error}
              </div>
            )}

            {/* Draw */}
            <div className="mt-4 flex items-center justify-center">
              <button
                type="button"
                onClick={draw}
                disabled={!!validateQuestion(q) || cooldown > 0}
                className={`px-7 py-2.5 rounded-full font-semibold 
              text-white border-2 shadow-lg
              transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5
              ${!!validateQuestion(q) || cooldown > 0 ? "opacity-60 cursor-not-allowed" : ""}`}
                style={{
                  backgroundImage: `linear-gradient(180deg, #512150, #512150)`, 
                  borderColor: "[#ffffff]", 
                }}
                onMouseEnter={(e) => {
                  if (!validateQuestion(q) && cooldown <= 0) {
                    e.currentTarget.style.backgroundImage = `linear-gradient(180deg, #512150, #bb4db9ff)`;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!validateQuestion(q) && cooldown <= 0) {
                    e.currentTarget.style.backgroundImage = `linear-gradient(180deg, #512150, #512150)`;
                  }
                }}
              >
                {cooldown > 0 ? `Wait ${cooldown}s` : "Draw Card"}
              </button>
            </div>

            {/* Result */}
            <div className="mt-8">
              {!result ? (
                <div className="min-h-[100px] grid place-items-center">
                  <p className="text-white/90 text-center max-w-[60ch]">
                    Enter a close-ended question (answerable by Yes/No/Wait)
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                  {/* Card (left desktop, second mobile) */}
                  <div className="order-2 md:order-1 flex flex-col items-center md:items-start">
                    {result.slug ? (
                      <Link to={`/tarot-card/${result.slug}`} className="block" aria-label={`Open ${result.cardName}`}>
                        <img
                          src={cardImageUrlFromSlug(result.slug)}
                          alt={result.cardName}
                          className={`w-[65vw] max-w-[160px] sm:max-w-[220px] md:max-w-[260px] lg:max-w-[260px] h-auto object-contain drop-shadow-[0_12px_30px_rgba(0,0,0,0.45)] transition-transform duration-500 ${
                            isFlipping ? "[transform:rotateY(180deg)] [transform-style:preserve-3d]" : ""
                          }`}
                          draggable="false"
                        />
                        <div className="mt-2 text-center md:text-center font-normal underline underline-offset-4 text-white/90">
                          {result.cardName}
                        </div>
                      </Link>
                    ) : (
                      <div className="font-semibold text-white/90">{result.cardName}</div>
                    )}

                    {/* MOBILE share/copy directly under card */}
                    <div className="flex md:hidden gap-3 pt-6">
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await navigator.share?.({
                              title: "Yes / No Tarot",
                              text: `Q: ${q}\nResult: ${result.verdict} — ${result.cardName}\n${result.reason}`,
                            });
                          } catch {}
                        }}
                        className="text-sm text-white bg-[#512150] hover:bg-[#512150]/50 border border-white hover:border-white px-4 py-1.5 rounded-full transition shadow-md hover:shadow-lg"
                      >
                        🔗 Share
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const text = `Yes / No Tarot\nQ: ${q}\nResult: ${result.verdict} — ${result.cardName}\n${result.reason}`;
                          navigator.clipboard.writeText(text);
                          setShowCopiedToast(true);
                          setTimeout(() => setShowCopiedToast(false), 1800);
                        }}
                        className="text-sm text-white bg-[#512150] hover:bg-[#512150]/50 border border-white hover:border-white px-4 py-1.5 rounded-full transition shadow-md hover:shadow-lg"
                      >
                        🧾 Copy
                      </button>
                    </div>
                    {showCopiedToast && <div className="md:hidden mt-1 text-xs text-white/90">Copied ✓</div>}
                  </div>

                  {/* Text (right desktop, first mobile) */}
                  <div className="order-1 md:order-2 flex flex-col items-center md:items-start text-center md:text-left gap-3">
                    <div className="inline-flex items-center gap-2 bg-white/95 border border-white rounded-md px-4 py-2 shadow-[0_4px_14px_rgba(0,0,0,0.15)]">
                      <span className="text-[#0f3d3f] font-semibold text-lg">Result:</span>
                      <span className={`font-bold text-lg ${
                        result?.verdict === "YES" ? "text-emerald-700" : result?.verdict === "NO" ? "text-rose-700" : "text-amber-700"
                      }`}>{result.verdict}</span>
                    </div>

                    <p className="mt-1 text-white/95 max-w-[70ch]">
                      {loadingReason ? "Gathering guidance…" : result.reason}
                    </p>

                    {/* DESKTOP share/copy beside text */}
                    <div className="hidden md:flex gap-3 pt-2">
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await navigator.share?.({
                              title: "Yes / No Tarot",
                              text: `Q: ${q}\nResult: ${result.verdict} — ${result.cardName}\n${result.reason}`,
                            });
                          } catch {}
                        }}
                        className="text-sm text-white bg-[#512150] hover:bg-[#512150]/50 border border-white hover:border-white px-4 py-1.5 rounded-full transition shadow-md hover:shadow-lg"
                      >
                        🔗 Share
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const text = `Yes / No Tarot\nQ: ${q}\nResult: ${result.verdict} — ${result.cardName}\n${result.reason}`;
                          navigator.clipboard.writeText(text);
                          setShowCopiedToast(true);
                          setTimeout(() => setShowCopiedToast(false), 1800);
                        }}
                        className="text-sm text-white bg-[#512150] hover:bg-[#512150]/50 border border-white hover:border-white px-4 py-1.5 rounded-full transition shadow-md hover:shadow-lg"
                      >
                        🧾 Copy
                      </button>
                    </div>
                    {showCopiedToast && <div className="hidden md:block mt-1 text-xs text-white/90">Copied ✓</div>}
                  </div>
                </div>
              )}
            </div>

          </div>
        </section>
        {/* Divider + How it works */}
        <div className="my-10 h-px w-full bg-gradient-to-r from-transparent via-white/30 to-transparent" />
        <section className="max-w-4xl mx-auto">
          <h2 className="text-xl font-semibold text-white">How Yes / No Tarot Works</h2>
          <ol className="list-decimal ml-5 mt-2 space-y-2 text-white/90">
            <li><strong>Ask one focused question.</strong> Keep it clear and anchored to a single situation.</li>
            <li><strong>Draw one card.</strong> The result is <em>Yes</em>, <em>No</em>, or <em>Wait</em>, plus a short reflective message.</li>
            <li><strong>Reflect, then act.</strong> Treat it as a lens for decision-making, not prediction.</li>
          </ol>

          <h3 className="mt-5 text-lg font-semibold text-white">What kind of question?</h3>
          <ul className="list-disc ml-5 mt-2 space-y-1 text-white/90">
            <li><strong>Close-ended:</strong> Answerable by Yes / No / Wait.</li>
            <li><strong>Time-boxed:</strong> Add a near horizon like “this week” or “this month”.</li>
            <li><strong>Action-ready:</strong> You can take a next step after reading.</li>
          </ul>
          <p className="mt-2 text-white/90"><em>Good:</em> “Should I apply to the new role this week?” • “Is it wise to reach out today?”</p>
          <p className="text-white/90"><em>Avoid:</em> Multilayered or vague questions such as “What’s my destiny?”</p>

          <h3 className="mt-5 text-lg font-semibold text-white">Purpose</h3>
          <p className="text-white/90">
            Yes/No Tarot offers a quick directional signal. It’s a reflective tool to clarify your next small step, not a guarantee of outcomes.
          </p>

          <h3 className="mt-5 text-lg font-semibold text-white">Notes</h3>
          <ul className="list-disc ml-5 mt-2 space-y-1 text-white/90">
            <li><strong>Deterministic:</strong> The same question usually yields the same result for the day.</li>
            <li><strong>Cooldown:</strong> Draw again after 60 seconds with a refined question.</li>
            <li><strong>Ethics:</strong> Avoid medical, legal, or financial dependency. Seek qualified help when needed.</li>
            <li><strong>Privacy:</strong> Your question is only used to generate guidance; avoid sensitive details.</li>
          </ul>
        </section>
      </main>
    </div>
  );
}
